import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { AlertCircle, Loader2, Plus } from "lucide-react";
import { ACCENT, fmtDate, humanize, Field, HistoryTable } from "./shared";

export default function ChemoTab({ data, ref_, onSaved }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    regimen_code: "TCH",
    cycle_number: 1,
    total_cycles: 6,
    dose_reduction_percent: 0,
    delayed: false,
    toxicities: [],
    next_cycle_date: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const selectedRegimen = useMemo(
    () => ref_.regimens.find((r) => r.code === form.regimen_code),
    [ref_.regimens, form.regimen_code]
  );

  useEffect(() => {
    if (selectedRegimen) {
      setForm((f) => ({ ...f, total_cycles: selectedRegimen.total_cycles }));
    }
  }, [selectedRegimen]);

  function toggleToxicity(t) {
    setForm((f) => ({
      ...f,
      toxicities: f.toxicities.includes(t)
        ? f.toxicities.filter((x) => x !== t) : [...f.toxicities, t],
    }));
  }

  async function submit(e) {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      await api.post("/specialties/oncology/chemo", {
        date: form.date,
        regimen_code: form.regimen_code,
        cycle_number: Number(form.cycle_number),
        total_cycles: Number(form.total_cycles),
        dose_reduction_percent: Number(form.dose_reduction_percent),
        delayed: form.delayed,
        toxicities: form.toxicities,
        next_cycle_date: form.next_cycle_date || null,
        notes: form.notes || null,
      });
      toast.success("Cycle logged");
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to log cycle");
    } finally { setSaving(false); }
  }

  const s = data.summary || {};
  const barData = useMemo(
    () => [...data.cycles].slice().reverse().map((c) => ({
      cycle: `C${c.cycle_number}`,
      Toxicities: (c.toxicities || []).length,
      "Dose Δ%": c.dose_reduction_percent || 0,
    })),
    [data.cycles]
  );

  return (
    <div className="space-y-6" data-testid="onco-chemo-tab">
      {s.current_regimen && (
        <div className="ros-card" data-testid="chemo-progress-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="ros-label">Active regimen</div>
              <div className="font-heading text-2xl text-white mt-1">{s.current_regimen}</div>
              <div className="text-xs text-[#A3ACA7] mt-1">
                {s.current_phase} · {s.current_progress_pct}% complete · Next cycle {fmtDate(s.next_cycle_date)}
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-heading font-bold" style={{ color: ACCENT }}>
                {s.current_progress_pct}%
              </div>
              <div className="text-[10px] text-[#A3ACA7]">complete</div>
            </div>
          </div>
          <div className="mt-3 h-3 rounded-full bg-[#1C201E] overflow-hidden">
            <div className="h-full transition-all"
                 style={{ width: `${Math.min(100, s.current_progress_pct || 0)}%`, background: ACCENT }} />
          </div>
        </div>
      )}

      <form onSubmit={submit} className="ros-card space-y-4" data-testid="chemo-form">
        <div className="font-heading text-lg">Log chemotherapy cycle</div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Cycle date">
            <input type="date" value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })}
                   data-testid="chemo-input-date" className="ros-input" required />
          </Field>
          <Field label="Regimen">
            <select value={form.regimen_code}
                    onChange={(e) => setForm({ ...form, regimen_code: e.target.value })}
                    data-testid="chemo-input-regimen" className="ros-input">
              {ref_.regimens.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.name} — {humanize(r.cancer)} ({r.cycle_length_days}d × {r.total_cycles})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Cycle #">
            <div className="flex gap-2 items-center">
              <input type="number" min={1} max={form.total_cycles}
                     value={form.cycle_number}
                     onChange={(e) => setForm({ ...form, cycle_number: e.target.value })}
                     data-testid="chemo-input-cycle-num"
                     className="ros-input" style={{ maxWidth: 70 }} required />
              <span className="text-sm text-[#A3ACA7]">of</span>
              <input type="number" min={1} value={form.total_cycles}
                     onChange={(e) => setForm({ ...form, total_cycles: e.target.value })}
                     data-testid="chemo-input-total-cycles"
                     className="ros-input" style={{ maxWidth: 70 }} required />
            </div>
          </Field>
        </div>

        {selectedRegimen && (
          <div className="rounded-xl p-3 border border-[#272E2B]" data-testid="chemo-regimen-info">
            <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Agents in {selectedRegimen.name}</div>
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedRegimen.agents.map((a) => (
                <span key={a} className="text-xs bg-[#1C201E] text-[#D1D5DB] px-2.5 py-1 rounded-full">{a}</span>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Dose reduction (%)">
            <input type="number" step="5" min={0} max={100} value={form.dose_reduction_percent}
                   onChange={(e) => setForm({ ...form, dose_reduction_percent: e.target.value })}
                   data-testid="chemo-input-dose-reduction" className="ros-input" />
          </Field>
          <Field label="Next cycle date (auto-calculated if blank)">
            <input type="date" value={form.next_cycle_date}
                   onChange={(e) => setForm({ ...form, next_cycle_date: e.target.value })}
                   data-testid="chemo-input-next" className="ros-input" />
          </Field>
          <Field label="Delayed?">
            <label className="inline-flex items-center gap-2 mt-2 cursor-pointer text-white text-sm">
              <input type="checkbox" checked={form.delayed}
                     onChange={(e) => setForm({ ...form, delayed: e.target.checked })}
                     data-testid="chemo-input-delayed"
                     className="accent-[#FBBF24]" />
              Cycle was delayed
            </label>
          </Field>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7] mb-2">Toxicities (CTCAE v5)</div>
          <div className="flex flex-wrap gap-2" data-testid="chemo-toxicities">
            {ref_.toxicities.map((t) => {
              const on = form.toxicities.includes(t);
              return (
                <button type="button" key={t} onClick={() => toggleToxicity(t)}
                        data-testid={`chemo-toxicity-${t}`}
                        className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                          on ? "text-black border-transparent" : "text-[#A3ACA7] border-[#272E2B] hover:text-white"
                        }`}
                        style={on ? { background: ACCENT } : {}}>
                  {humanize(t)}
                </button>
              );
            })}
          </div>
        </div>

        <Field label="Notes">
          <textarea rows={2} value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="chemo-input-notes"
                    className="ros-input" />
        </Field>

        {error && (
          <div className="text-sm text-[#F87171] flex items-center gap-2" data-testid="chemo-error">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div>
          <button type="submit" disabled={saving}
                  data-testid="chemo-submit"
                  className="flex items-center gap-2 px-5 py-2 rounded-full text-black font-semibold disabled:opacity-50 transition-colors"
                  style={{ background: ACCENT }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {saving ? "Logging…" : "Log cycle"}
          </button>
        </div>
      </form>

      {barData.length > 0 && (
        <div className="ros-card" data-testid="chemo-chart">
          <div className="font-heading text-lg mb-3">Toxicities per cycle</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData} margin={{ top: 10, right: 16, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#272E2B" />
                <XAxis dataKey="cycle" stroke="#A3ACA7" fontSize={11} />
                <YAxis stroke="#A3ACA7" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0F1312", border: "1px solid #272E2B", borderRadius: 12 }} />
                <Bar dataKey="Toxicities" fill={ACCENT} />
                <Bar dataKey="Dose Δ%"    fill="#F87171" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <HistoryTable
        title={`Cycle history (${data.cycles.length})`}
        testId="chemo-history"
        rows={data.cycles}
        rowTestId={(r) => `chemo-row-${r.id}`}
        empty="No chemotherapy cycles logged."
        columns={[
          { label: "Date",   render: (r) => fmtDate(r.date) },
          { label: "Regimen", render: (r) => <span className="text-xs">{r.regimen_name}</span> },
          { label: "Cycle",  render: (r) => <span className="font-mono">C{r.cycle_number}/{r.total_cycles}</span> },
          { label: "%",      render: (r) => <span className="font-mono">{r.percent_complete}%</span> },
          { label: "Phase",  render: (r) => <span className="text-xs text-[#A3ACA7]">{r.phase}</span> },
          { label: "Dose Δ", render: (r) => (
            <span className="font-mono text-xs"
                  style={{ color: (r.dose_reduction_percent || 0) > 0 ? "#F87171" : "#4ADE80" }}>
              -{r.dose_reduction_percent || 0}%
            </span>
          )},
          { label: "Toxicities", render: (r) => (
            <div className="flex flex-wrap gap-1">
              {(r.toxicities || []).slice(0, 3).map((t) =>
                <span key={t} className="text-[10px] text-[#A3ACA7] bg-[#1C201E] px-1.5 py-0.5 rounded">{humanize(t)}</span>
              )}
            </div>
          )},
          { label: "Next", render: (r) => <span className="text-xs text-[#A3ACA7]">{fmtDate(r.next_cycle_date)}</span> },
        ]}
      />
    </div>
  );
}
